import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, Input, Space, Table, Tabs, Tag, Tooltip, Typography, message } from "antd";
import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";

import { broadcastsApi } from "../../../api/broadcastsApi";
import {
  BroadcastCounts,
  EXCLUDE_LABELS,
  ExcludeReason,
  IBroadcastRecipient,
  RecipientStatus,
} from "../../../types/models/broadcasts";

/**
 * The review table.
 *
 * Bad rows are shown rather than dropped, which is only worth doing if they can
 * be acted on — so the phone number and the greeting are editable in place, and
 * a corrected number is re-checked server-side and moves into whichever bucket
 * it now belongs to. The row that comes back from the API is authoritative; a
 * local guess at the new status would be wrong exactly when the fix introduced
 * a duplicate, which is the case worth catching.
 */

const Container = styled.div`
  .muted {
    color: rgba(0, 0, 0, 0.45);
  }

  .problem {
    color: #cf1322;
  }

  .row-excluded td {
    background: #fafafa;
  }

  .row-failed td {
    background: #fff1f0;
  }
`;

const { Search } = Input;

type TabKey = string;

interface Tab {
  key: TabKey;
  label: string;
  count: number;
  query: { status?: RecipientStatus; exclude_reason?: ExcludeReason };
}

const STATUS_LABELS: Partial<Record<RecipientStatus, string>> = {
  pending: "Ready to send",
  sending: "Sending…",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed",
};

/**
 * Which tabs to show, derived from the counts rather than hard-coded.
 *
 * A bucket with nothing in it is left out: a fresh import should not present
 * five empty tabs for problems this particular file does not have, and a list
 * that has not been sent yet has no reason to show "Delivered: 0".
 */
function tabsFor(counts: BroadcastCounts): Tab[] {
  const tabs: Tab[] = [
    { key: "all", label: "All rows", count: counts.total, query: {} },
  ];

  (Object.keys(STATUS_LABELS) as RecipientStatus[]).forEach((status) => {
    if (counts[status] > 0) {
      tabs.push({
        key: status,
        label: STATUS_LABELS[status]!,
        count: counts[status],
        query: { status },
      });
    }
  });

  (Object.keys(EXCLUDE_LABELS) as ExcludeReason[]).forEach((reason) => {
    if (counts[reason] > 0) {
      tabs.push({
        key: `excluded:${reason}`,
        label: EXCLUDE_LABELS[reason],
        count: counts[reason],
        query: { status: "excluded", exclude_reason: reason },
      });
    }
  });

  return tabs;
}

function RecipientTable({
  broadcastId,
  counts,
  editable,
  canTestSend = false,
  onChanged,
}: {
  broadcastId: number;
  counts: BroadcastCounts;
  /** False once sending starts — the list is frozen with the run. */
  editable: boolean;
  /** Whether a single test message may be sent. Only once a template is on. */
  canTestSend?: boolean;
  /** Fired after any edit, so the parent can refresh its counts. */
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<IBroadcastRecipient[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>("all");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<number | null>(null);

  const tabs = useMemo(() => tabsFor(counts), [counts]);
  const query = useMemo(
    () => tabs.find((t) => t.key === tab)?.query ?? {},
    [tabs, tab]
  );

  const load = useCallback(() => {
    setLoading(true);
    broadcastsApi
      .recipients({
        broadcast: broadcastId,
        search: search || undefined,
        page,
        page_size: 50,
        ...query,
      })
      .then((data) => {
        setRows(data.results);
        setTotal(data.count);
      })
      .catch(() => message.error("Could not load the contacts."))
      .finally(() => setLoading(false));
  }, [broadcastId, page, search, query]);

  useEffect(load, [load]);

  /** Swap one row in place, from the server's version of it. */
  const replace = (updated: IBroadcastRecipient) => {
    setRows((current) =>
      current.map((row) => (row.id === updated.id ? updated : row))
    );
    onChanged();
  };

  const act = (
    id: number,
    call: Promise<IBroadcastRecipient>,
    onConflict?: string
  ) => {
    setBusy(id);
    call
      .then(replace)
      .catch((error) => {
        if (error?.response?.status === 409 && onConflict) {
          message.warning(error.response.data?.detail ?? onConflict);
          // The server still revalidated the row and told us what it looks like
          // now; showing that is more useful than leaving a stale one on screen.
          const refreshed = error.response.data?.recipient;
          if (refreshed) replace(refreshed);
          return;
        }
        message.error("That change could not be saved.");
      })
      .finally(() => setBusy(null));
  };

  const columns = [
    {
      title: "Row",
      dataIndex: "row_number",
      width: 70,
      // The number of the line in the file they were handed, so "row 43" can be
      // looked up in the spreadsheet.
      render: (n: number) => <span className="muted">{n}</span>,
    },
    {
      title: "Name in the file",
      dataIndex: "name_raw",
      ellipsis: true,
      render: (text: string) => text || <span className="muted">—</span>,
    },
    {
      title: "Greeting",
      dataIndex: "greeting",
      width: 150,
      render: (text: string, row: IBroadcastRecipient) =>
        editable ? (
          <Typography.Text
            editable={{
              onChange: (value) =>
                value !== text &&
                act(row.id, broadcastsApi.updateRecipient(row.id, { greeting: value })),
              tooltip: "What goes in the message",
            }}
          >
            {text}
          </Typography.Text>
        ) : (
          text || <span className="muted">—</span>
        ),
    },
    {
      title: "Phone in the file",
      dataIndex: "phone_raw",
      width: 190,
      render: (text: string, row: IBroadcastRecipient) =>
        editable ? (
          <Typography.Text
            editable={{
              onChange: (value) =>
                value !== text &&
                act(row.id, broadcastsApi.updateRecipient(row.id, { phone_raw: value })),
              tooltip: "Fix the number",
            }}
          >
            {text}
          </Typography.Text>
        ) : (
          text || <span className="muted">—</span>
        ),
    },
    {
      title: "Will send to",
      dataIndex: "phone_e164",
      width: 180,
      render: (text: string, row: IBroadcastRecipient) =>
        text ? (
          <Space size={4}>
            <span>{text}</span>
            {row.phone_region && <Tag className="muted">{row.phone_region}</Tag>}
          </Space>
        ) : (
          <span className="problem">{row.exclude_label || "Not sendable"}</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 160,
      render: (status: RecipientStatus, row: IBroadcastRecipient) => {
        if (status === "excluded") {
          return <Tag>{row.exclude_label || "Excluded"}</Tag>;
        }
        if (status === "failed") {
          return (
            // The Meta error code is the only reliable way to tell "not a
            // WhatsApp user" from "your template was deleted", so it is on the
            // row rather than buried in a log.
            <Tooltip title={row.error || "Failed"}>
              <Tag color="red">
                Failed{row.error_code ? ` (${row.error_code})` : ""}
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tag color={status === "pending" ? "blue" : "green"}>
            {STATUS_LABELS[status] ?? status}
          </Tag>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 170,
      render: (_: any, row: IBroadcastRecipient) => {
        if (!editable) return null;
        if (row.status === "excluded") {
          return (
            <Button
              size="small"
              type="link"
              icon={<PlusCircleOutlined />}
              loading={busy === row.id}
              onClick={() =>
                act(
                  row.id,
                  broadcastsApi.includeRecipient(row.id),
                  "That row still cannot be sent to."
                )
              }
            >
              Include
            </Button>
          );
        }
        if (row.status !== "pending") return null;
        return (
          <Space size={0}>
            {canTestSend && (
              // The point of putting your own number in the list. It takes the
              // same code path as the run and consumes the row, so the run
              // will not send to them again.
              <Tooltip title="Send this one message now">
                <Button
                  size="small"
                  type="link"
                  icon={<SendOutlined />}
                  loading={busy === row.id}
                  onClick={() => act(row.id, broadcastsApi.testSend(row.id))}
                >
                  Test
                </Button>
              </Tooltip>
            )}
            <Button
              size="small"
              type="link"
              danger
              icon={<MinusCircleOutlined />}
              loading={busy === row.id}
              onClick={() => act(row.id, broadcastsApi.excludeRecipient(row.id))}
            >
              Remove
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Container>
      <Tabs
        activeKey={tab}
        onChange={(key) => {
          setTab(key);
          setPage(1);
        }}
        tabBarExtraContent={
          <Search
            allowClear
            placeholder="Name, phone…"
            style={{ width: 240 }}
            onSearch={(value) => {
              setSearch(value);
              setPage(1);
            }}
          />
        }
      >
        {tabs.map((t) => (
          <Tabs.TabPane key={t.key} tab={`${t.label} (${t.count})`} />
        ))}
      </Tabs>

      <Table<IBroadcastRecipient>
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={rows}
        columns={columns as any}
        rowClassName={(row) =>
          row.status === "failed"
            ? "row-failed"
            : row.status === "excluded"
            ? "row-excluded"
            : ""
        }
        pagination={{
          current: page,
          pageSize: 50,
          total,
          showSizeChanger: false,
          onChange: setPage,
          showTotal: (n) => `${n} contacts`,
        }}
        scroll={{ x: 1000 }}
      />
    </Container>
  );
}

export default RecipientTable;
